<script>
import ListNotificationsByAccountComponent from '../../features/Lists/ListNotificationsByAccountComponent';
import ModalNotificationAnswerActivity from '../modals/ModalNotificationAnswerActivityView';
import ModalStudentInfoView from '../../../account/views/ModalStudentInfoView';
import ListComponent from '../../../../core/infraestructure/ListComponent';
import { EventBus } from '../../../../core/shared/helpers/EventHelper';

export default {
  components: {
    'modal-notification-answer-activity-view': ModalNotificationAnswerActivity,
    'modal-student-info-view': ModalStudentInfoView,
  },
  extends: ListNotificationsByAccountComponent,
  data() {
    return {
      readFilter: 'all',
      totalUnread: 0,
      isMarkingAll: false,
    };
  },
  computed: {
    // El total de no leidas lo calcula el servidor sobre el conjunto completo
    // de la cuenta, asi que no depende de la pagina visible.
    unreadCount() {
      return this.totalUnread;
    },
    // El filtro de lectura se aplica en el servidor (GET .../by-account?IsRead=)
    // para que filtro y paginacion sean coherentes entre si.
    filteredNotifications() {
      return this.collection || [];
    },
  },
  methods: {
    /**
     * La respuesta paginada trae `totalUnread`: el total de notificaciones no
     * leidas de la cuenta, calculado sin el filtro activo.
     */
    onSearchResponse(response, headers) {
      this.totalUnread = typeof response.totalUnread === 'number' ? response.totalUnread : 0;
      ListComponent.methods.onSearchResponse.call(this, response, headers);
    },
    onReadFilterChange(filter) {
      if (this.readFilter === filter) {
        return;
      }
      this.readFilter = filter;
      // "No leidas" -> IsRead=false. "Todas" -> null elimina el filtro.
      this.viewModel.updateFilters(filter === 'unread' ? false : null, 'IsRead', '=');
      this.currentPage = 1;
      this.onSearch();
    },
    onItemClick(notification) {
      if (notification.url) {
        this.onSelectNotification(notification);
        return;
      }
      // Sin destino al que navegar: el clic en el contenido marca la leida.
      this.onMarkAsRead(notification);
    },
    onMarkAsRead(notification) {
      if (!notification || notification.isRead) {
        return;
      }
      this.viewModel.doMarkAsReadSilently(notification);
    },
    onMarkAsReadSilentResponse(notification) {
      if (!notification) {
        return;
      }
      notification.isRead = true;
      if (this.readFilter === 'unread') {
        this.collection = this.collection.filter(n => n.id !== notification.id);
        // El contador de paginacion se recalcula sobre el conjunto filtrado.
        this.pagination.totalItems = Math.max(0, this.pagination.totalItems - 1);
        this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.limit);
      }
      this.totalUnread = Math.max(0, this.totalUnread - 1);
      // Se avisa a la campana del sidebar para que su contador no quede viejo.
      EventBus.$emit('notifications-updated');
    },
    onMarkAllAsRead() {
      if (this.unreadCount === 0 || this.isMarkingAll) {
        return;
      }
      this.isMarkingAll = true;
      this.viewModel.doMarkAllAsRead();
    },
    onMarkAllAsReadResponse(response) {
      this.isMarkingAll = false;
      this.onShowSuccess(response.message);
      this.currentPage = 1;
      this.onSearch();
      EventBus.$emit('notifications-updated');
    },
    senderName(sender) {
      if (!sender) return 'Sistema';
      const name = `${sender.firstName || ''} ${sender.lastName || ''}`.trim();
      return name || 'Sistema';
    },
    onAvatarError(event) {
      event.target.src = '/images/left-imgs/img-1.jpg';
    },
  },
};
</script>

<template>
  <!-- Body Start -->
  <div class="wrapper">
    <div class="sa4d25">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <div class="section3125">
              <!-- Encabezado de página -->
              <div class="stream_page_header">
                <div class="stream_header_left">
                  <div class="stream_header_icon">
                    <i class="fas fa-bell" />
                  </div>
                  <div class="stream_header_text">
                    <h4 class="item_title">
                      Notificaciones
                    </h4>
                    <p class="stream_header_subtitle">
                      Avisos de tus cursos, actividades revisadas y novedades de la plataforma
                    </p>
                  </div>
                </div>
              </div>

              <!-- Barra de filtros -->
              <div class="stream_toolbar">
                <div class="noti_segmented">
                  <button
                    type="button"
                    :class="{ is_active: readFilter === 'all' }"
                    @click="onReadFilterChange('all')"
                  >
                    Todas
                  </button>
                  <button
                    type="button"
                    :class="{ is_active: readFilter === 'unread' }"
                    @click="onReadFilterChange('unread')"
                  >
                    No leídas
                    <span
                      v-if="unreadCount > 0"
                      class="noti_segmented_count"
                    >{{ unreadCount }}</span>
                  </button>
                </div>
                <span
                  v-if="unreadCount > 0"
                  class="stream_count_badge"
                >
                  <i class="fas fa-circle" />
                  <strong>{{ unreadCount }}</strong> sin leer
                </span>
                <button
                  type="button"
                  class="noti_mark_all_btn"
                  :disabled="unreadCount === 0 || isMarkingAll"
                  @click="onMarkAllAsRead"
                >
                  <i class="fas fa-check-double" />
                  {{ isMarkingAll ? 'Marcando...' : 'Marcar todas como leídas' }}
                </button>
              </div>

              <!-- Lista de notificaciones -->
              <div
                v-loading="isListLoading"
                element-loading-text="Cargando notificaciones..."
                class="noti_list"
              >
                <div
                  v-for="notification in filteredNotifications"
                  :key="notification.id"
                  class="noti_item"
                  :class="{ is_unread: !notification.isRead, is_clickable: !!notification.url }"
                  @click="onItemClick(notification)"
                >
                  <img
                    :src="mapImage(notification.sender)"
                    :alt="senderName(notification.sender)"
                    class="noti_avatar"
                    @error="onAvatarError"
                  >
                  <div class="noti_content">
                    <div class="noti_meta">
                      <span class="noti_sender">{{ senderName(notification.sender) }}</span>
                      <span
                        v-if="!notification.isRead"
                        class="noti_new_badge"
                      >
                        <span class="noti_new_dot" /> Nuevo
                      </span>
                    </div>
                    <h6 class="noti_title">
                      {{ notification.header }}
                    </h6>
                    <p
                      v-if="notification.message"
                      class="noti_message"
                    >
                      {{ notification.message }}
                    </p>
                  </div>
                  <div class="noti_actions">
                    <button
                      v-if="!notification.isRead"
                      type="button"
                      class="noti_mark_btn"
                      @click.stop="onMarkAsRead(notification)"
                    >
                      <i class="fas fa-check" /> Marcar como leída
                    </button>
                    <div
                      v-if="notification.url"
                      class="noti_action"
                    >
                      <span class="noti_action_btn">
                        Ver detalle <i class="fas fa-arrow-right" />
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Estado vacío -->
                <div
                  v-if="!isListLoading && filteredNotifications.length === 0"
                  class="stream_empty_state"
                >
                  <div class="stream_empty_icon">
                    <i class="far fa-bell-slash" />
                  </div>
                  <h4>{{ readFilter === 'unread' ? 'Estás al día' : 'No tienes notificaciones' }}</h4>
                  <p>
                    {{ readFilter === 'unread'
                      ? 'No tienes notificaciones sin leer.'
                      : 'Cuando tus profesores revisen tus actividades o haya novedades, aparecerán aquí.' }}
                  </p>
                </div>
              </div>

              <!-- Paginación -->
              <div
                v-if="!isCollectionEmpty(collection) && pagination.totalPages > 1"
                class="d-flex justify-content-center mt-3 mb-4"
              >
                <el-pagination
                  v-model:current-page="currentPage"
                  background
                  layout="prev, pager, next"
                  :page-count="pagination.totalPages"
                  @current-change="onPageChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <modal-notification-answer-activity-view
      v-if="modalItemAnswerActivity.isVisible"
      :form-data="itemAnswerActivity"
      :conf="modalItemAnswerActivity"
    />
    <modal-student-info-view
      v-if="modalSubscriptionInfo.isVisible"
      :form-data="studentInfoItem"
      :conf="modalSubscriptionInfo"
    />

  </div>
  <!-- Body End -->
</template>
